import React from 'react';
import {
  SubFleetPayload,
  SubFleetResponse,
  activateSubFleet,
  createSubFleet,
  deactivateSubFleet,
  deleteSubFleet,
  getSubFleet,
  getSubFleets,
  getDrivers,
  getVehicles,
  updateSubFleet,
} from '../../services/fleet';

type FleetOption = { id: string; name: string };
type SubFleetRecord = SubFleetResponse & { fleetName: string };
type SelectOption = { id: string; label: string; meta: string };
const PAGE_SIZE = 50;

export function SubFleetWorkspace({ fleets }: { fleets: FleetOption[] }) {
  const [subFleets, setSubFleets] = React.useState<SubFleetRecord[]>([]);
  const [selected, setSelected] = React.useState<SubFleetRecord | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [fleetId, setFleetId] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [offset, setOffset] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');

  const load = React.useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await getSubFleets({
        search: search.trim() || undefined,
        fleet_id: fleetId || undefined,
        status: status ? status as 'active' | 'inactive' : undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setSubFleets(response.subfleets.map((item) => normalizeSubFleet(item, fleets)));
      setTotal(response.total);
    } catch (requestError) {
      setError(getError(requestError, 'Unable to load sub-fleets'));
    } finally {
      setIsLoading(false);
    }
  }, [fleetId, fleets, offset, search, status]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => void load(), 250);
    return () => window.clearTimeout(timer);
  }, [load]);

  async function manage(subFleetId: string) {
    setError('');
    try {
      setSelected(normalizeSubFleet(await getSubFleet(subFleetId), fleets));
    } catch (requestError) {
      setError(getError(requestError, 'Unable to load sub-fleet'));
    }
  }

  return (
    <>
      <div className="fleet-organization-head">
        <div><h2>Sub-fleets</h2><p>Group selected drivers and vehicles for focused operations.</p></div>
        <button className="btn btn-primary fleet-create-btn" onClick={() => setIsCreating(true)} type="button"><span aria-hidden="true">+</span> Create sub-fleet</button>
      </div>
      {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}
      {message ? <div className="kyc-message" role="status">{message}</div> : null}
      <div className="sub-fleet-filters">
        <label className="sub-fleet-search"><span aria-hidden="true">S</span><input aria-label="Search sub-fleets" onChange={(event) => { setOffset(0); setSearch(event.target.value); }} placeholder="Search sub-fleets..." value={search} /></label>
        <label><span className="sr-only">Filter by fleet</span><select aria-label="Filter by fleet" onChange={(event) => { setOffset(0); setFleetId(event.target.value); }} value={fleetId}><option value="">All fleets</option>{fleets.map((fleet) => <option key={fleet.id} value={fleet.id}>{fleet.name}</option>)}</select></label>
        <label><span className="sr-only">Filter by status</span><select aria-label="Filter by status" onChange={(event) => { setOffset(0); setStatus(event.target.value); }} value={status}><option value="">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
        <button className="btn" disabled={isLoading} onClick={() => void load()} type="button">Refresh</button>
      </div>
      {isLoading ? <div className="empty"><h3>Loading sub-fleets</h3></div> : subFleets.length ? (
        <section className="sub-fleet-grid" aria-label="Sub-fleets">
          {subFleets.map((subFleet) => <SubFleetCard key={subFleet.id} onManage={() => void manage(subFleet.id)} subFleet={subFleet} />)}
        </section>
      ) : <div className="empty"><h3>No sub-fleets found</h3><p>Create a sub-fleet to group selected drivers and vehicles.</p></div>}
      <div className="passengers-pagination"><span>Showing {total ? offset + 1 : 0}-{Math.min(offset + PAGE_SIZE, total)} of {total}</span><div><button className="btn" disabled={offset === 0 || isLoading} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} type="button">Previous</button><button className="btn" disabled={offset + PAGE_SIZE >= total || isLoading} onClick={() => setOffset(offset + PAGE_SIZE)} type="button">Next</button></div></div>
      {isCreating ? <SubFleetDialog fleets={fleets} onClose={() => setIsCreating(false)} onSaved={async (notice) => { setIsCreating(false); setMessage(notice); await load(); }} /> : null}
      {selected ? <SubFleetDialog fleets={fleets} onClose={() => setSelected(null)} onSaved={async (notice) => { setSelected(null); setMessage(notice); await load(); }} subFleet={selected} /> : null}
    </>
  );
}

function SubFleetCard({ subFleet, onManage }: { subFleet: SubFleetRecord; onManage: () => void }) {
  return (
    <article className="sub-fleet-card">
      <div className="sub-fleet-card-head"><span className="fleet-organization-icon fleet"><TruckIcon /></span><div><strong>{subFleet.name}</strong><span>{subFleet.fleetName || 'Unassigned fleet'}</span></div><span className={`pill ${subFleet.status === 'active' ? 'success' : 'muted'}`}>{subFleet.status}</span></div>
      <p>{subFleet.description || 'No description provided'}</p>
      <div className="sub-fleet-metrics"><Metric label="Drivers" value={subFleet.driver_ids.length} /><Metric label="Vehicles" value={subFleet.vehicle_ids.length} /></div>
      <button className="btn sub-fleet-manage" onClick={onManage} type="button">Manage sub-fleet</button>
    </article>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return <div><strong>{value}</strong><span>{label}</span></div>;
}

function SubFleetDialog({ fleets, subFleet, onClose, onSaved }: { fleets: FleetOption[]; subFleet?: SubFleetRecord; onClose: () => void; onSaved: (message: string) => Promise<void> }) {
  const [form, setForm] = React.useState<SubFleetPayload>({ name: subFleet?.name ?? '', fleet_id: subFleet?.fleet_id ?? '', description: subFleet?.description ?? '', driver_ids: subFleet?.driver_ids ?? [], vehicle_ids: subFleet?.vehicle_ids ?? [] });
  const [drivers, setDrivers] = React.useState<SelectOption[]>([]);
  const [vehicles, setVehicles] = React.useState<SelectOption[]>([]);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let isActive = true;
    Promise.all([getDrivers({ fleet_id: form.fleet_id || undefined, status: 'active' }), getVehicles()])
      .then(([driverResponse, vehicleResponse]) => {
        if (!isActive) return;
        setDrivers(normalizeDrivers(driverResponse));
        setVehicles(normalizeVehicles(vehicleResponse));
      })
      .catch((requestError) => {
        if (isActive) setError(getError(requestError, 'Unable to load drivers or vehicles'));
      });
    return () => {
      isActive = false;
    };
  }, [form.fleet_id]);

  async function run(action: () => Promise<unknown>, notice: string) {
    setIsSubmitting(true);
    setError('');
    try {
      await action();
      await onSaved(notice);
    } catch (requestError) {
      setError(getError(requestError, 'Unable to save sub-fleet'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose} role="presentation">
      <form className="signup-dialog sub-fleet-dialog" onMouseDown={(event) => event.stopPropagation()} onSubmit={(event) => { event.preventDefault(); void run(() => subFleet ? updateSubFleet(subFleet.id, form) : createSubFleet(form), subFleet ? 'Sub-fleet updated successfully.' : 'Sub-fleet created successfully.'); }} role="dialog" aria-modal="true">
        <div className="signup-dialog-head"><h2>{subFleet ? `Edit ${subFleet.name}` : 'Create sub-fleet'}</h2><button aria-label="Close sub-fleet dialog" className="btn-icon borderless" onClick={onClose} type="button">&times;</button></div>
        {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}
        <div className="sub-fleet-form-grid">
          <label className="form-group"><span>Name</span><input required maxLength={120} placeholder="e.g. Island Morning" onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} value={form.name} /></label>
          <label className="form-group"><span>Fleet</span><select required onChange={(event) => setForm((current) => ({ ...current, fleet_id: event.target.value }))} value={form.fleet_id}><option value="">No fleet</option>{fleets.map((fleet) => <option key={fleet.id} value={fleet.id}>{fleet.name}</option>)}</select></label>
          <label className="form-group sub-fleet-description"><span>Description <small>Optional</small></span><textarea aria-label="Description" onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} value={form.description ?? ''} /></label>
          <SelectableList title="Drivers" options={drivers} selectedIds={form.driver_ids ?? []} onChange={(ids) => setForm((current) => ({ ...current, driver_ids: ids }))} />
          <SelectableList title="Vehicles" options={vehicles} selectedIds={form.vehicle_ids ?? []} onChange={(ids) => setForm((current) => ({ ...current, vehicle_ids: ids }))} />
        </div>
        <div className="kyc-dialog-actions sub-fleet-dialog-actions">
          <button className="btn" onClick={onClose} type="button">Cancel</button>
          {subFleet ? <button className="btn" disabled={isSubmitting} onClick={() => void run(() => subFleet.status === 'active' ? deactivateSubFleet(subFleet.id) : activateSubFleet(subFleet.id), `Sub-fleet ${subFleet.status === 'active' ? 'deactivated' : 'activated'} successfully.`)} type="button">{subFleet.status === 'active' ? 'Deactivate' : 'Activate'}</button> : null}
          {subFleet ? confirmDelete ? <button className="btn btn-danger" disabled={isSubmitting} onClick={() => void run(() => deleteSubFleet(subFleet.id), 'Sub-fleet deleted successfully.')} type="button">Confirm delete</button> : <button className="btn btn-danger" onClick={() => setConfirmDelete(true)} type="button">Delete</button> : null}
          <button className="btn btn-primary" disabled={isSubmitting || !form.fleet_id} type="submit">{isSubmitting ? 'Saving...' : subFleet ? 'Save changes' : 'Create sub-fleet'}</button>
        </div>
      </form>
    </div>
  );
}

function SelectableList({ title, options, selectedIds, onChange }: { title: string; options: SelectOption[]; selectedIds: string[]; onChange: (ids: string[]) => void }) {
  return (
    <section className="sub-fleet-select-list" aria-label={title}>
      <h3>{title}</h3>
      <div>
        {options.length ? options.map((option) => {
          const checked = selectedIds.includes(option.id);
          return (
            <label key={option.id}>
              <input
                checked={checked}
                onChange={() => onChange(checked ? selectedIds.filter((id) => id !== option.id) : [...selectedIds, option.id])}
                type="checkbox"
              />
              <span><strong>{option.label}</strong><small>{option.meta}</small></span>
            </label>
          );
        }) : <p>No {title.toLowerCase()} available.</p>}
      </div>
    </section>
  );
}

function normalizeSubFleet(item: SubFleetResponse, fleets: FleetOption[]): SubFleetRecord {
  return { ...item, driver_ids: item.driver_ids ?? [], vehicle_ids: item.vehicle_ids ?? [], fleetName: fleets.find((fleet) => fleet.id === item.fleet_id)?.name ?? '' };
}

function getError(error: unknown, fallback: string) { return error instanceof Error ? error.message : fallback; }
function normalizeDrivers(response: unknown): SelectOption[] {
  return getArray(response, ['drivers', 'items', 'results', 'data']).map((driver, index) => ({
    id: readString(driver, 'id', 'driver_id') ?? String(index),
    label: readString(driver, 'full_name', 'name', 'driver_name', 'email') ?? 'Unnamed driver',
    meta: readString(driver, 'status') ?? 'active',
  }));
}
function normalizeVehicles(response: unknown): SelectOption[] {
  return getArray(response, ['vehicles', 'items', 'results', 'data']).map((vehicle, index) => {
    const make = readString(vehicle, 'make', 'manufacturer', 'brand') ?? '';
    const model = readString(vehicle, 'model') ?? '';
    return {
      id: readString(vehicle, 'id', 'vehicle_id') ?? String(index),
      label: readString(vehicle, 'registration_number', 'plate_number', 'plateNumber', 'name') ?? 'Unnamed vehicle',
      meta: [make, model].filter(Boolean).join(' ') || readString(vehicle, 'status') || 'vehicle',
    };
  });
}
function getArray(value: unknown, keys: string[]): Record<string, unknown>[] {
  if (Array.isArray(value)) return value.filter(isObject);
  if (!isObject(value)) return [];
  for (const key of keys) {
    const nested = value[key];
    if (Array.isArray(nested)) return nested.filter(isObject);
    if (isObject(nested)) {
      const records = getArray(nested, keys);
      if (records.length) return records;
    }
  }
  return [];
}
function readString(source: Record<string, unknown>, ...keys: string[]) { for (const key of keys) if (typeof source[key] === 'string' && source[key]) return source[key] as string; return undefined; }
function isObject(value: unknown): value is Record<string, unknown> { return Boolean(value) && typeof value === 'object' && !Array.isArray(value); }
function TruckIcon() { return <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h12v10H3zM15 10h4l2 3v3h-6z" /><circle cx="7" cy="18" r="2" /><circle cx="18" cy="18" r="2" /></svg>; }
